import { useEffect, useMemo, useState } from 'react';

import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';

import { getIntlLocale } from '@/shared/i18n';
import { usePermissions } from '@/shared/permissions';
import { useSession } from '@/shared/session';
import {
  CompactTablePagination,
  EmptyState,
  ErrorState,
  LoadingState,
  PageContainer,
  TablePageShell,
} from '@/shared/ui';
import {
  ChannelBadge,
  ConversationStatusChip,
  EscalationReasonChip,
  formatDayTime,
  formatDurationShort,
  IdleChip,
  RelativeTime,
  relativeTimeLabel,
} from '@/shared/ui/conversation';

import { OPERATOR_QUEUE_PARAMS } from '../../api/conversationsApi';
import { useConversations } from '../../hooks';
import { staffLabel } from '../../model/staff';
import type {
  ConversationChannel,
  ConversationStatus,
  ConversationSummary,
} from '../../model/types';

type TabKey = 'waiting' | 'operator' | 'open' | 'closed';

const TAB_STATUS: Record<TabKey, ConversationStatus> = {
  waiting: 'WAITING_OPERATOR',
  operator: 'OPERATOR',
  open: 'OPEN',
  closed: 'CLOSED',
};

const REASON_OPTIONS = [
  'EXPLICIT_REQUEST',
  'LOW_CONFIDENCE',
  'LOOP_DETECTED',
  'POLICY_TOPIC',
  'AGENT_ESCALATED',
  'AI_UNAVAILABLE',
  'NO_TEMPLATE',
] as const;

const WAITING_WARNING_MS = 30 * 60_000;
const WAITING_ERROR_MS = 2 * 60 * 60_000;

/** A non-empty MUI Select sentinel for "All" — `''` is MUI's own "nothing selected" value and would
 * never render its MenuItem's label (see the shared/ui/conversation Select-empty-value gotcha). */
const ALL_FILTER_VALUE = 'ALL';

function customerLabel(
  row: ConversationSummary,
  canViewPii: boolean,
  t: TFunction,
): { label: string; hidden: boolean } {
  if (row.displayName) {
    return { label: row.displayName, hidden: false };
  }
  if (row.provisional) {
    return {
      label: t('list.row.provisional', {
        channel: t(`channel.${row.lastChannel}`, row.lastChannel),
      }),
      hidden: false,
    };
  }
  return { label: t('list.row.customer'), hidden: !canViewPii };
}

/** `/conversations` — the operator inbox (design §6). */
export function ConversationsListPage() {
  const { t } = useTranslation('conversations');
  const { hasPermission } = usePermissions();
  const { user } = useSession();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const canViewPii = hasPermission('customers.feature.pii-view');
  const canViewChatbotSettings = hasPermission('settings.chatbot.view');

  const tab = ((): TabKey => {
    const raw = params.get('tab');
    return raw === 'waiting' || raw === 'operator' || raw === 'open' || raw === 'closed'
      ? raw
      : 'waiting';
  })();
  const channelFilter = (params.get('channel') as ConversationChannel | null) ?? '';
  const customerIdFilter = params.get('customerId') ?? '';
  const [reasonFilter, setReasonFilter] = useState<string>('');
  const page = Number(params.get('page') ?? 0) || 0;
  const size = Number(params.get('size') ?? 20) || 20;

  function updateParams(patch: Record<string, string | null>) {
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        for (const [key, value] of Object.entries(patch)) {
          if (value === null || value === '') {
            next.delete(key);
          } else {
            next.set(key, value);
          }
        }
        return next;
      },
      { replace: true },
    );
  }

  const listParams = useMemo(
    () => ({
      status: TAB_STATUS[tab],
      channel: channelFilter || undefined,
      customerId: customerIdFilter || undefined,
      page,
      size,
    }),
    [tab, channelFilter, customerIdFilter, page, size],
  );

  const query = useConversations(listParams);
  const queueCount = useConversations(OPERATOR_QUEUE_PARAMS);

  const rows = useMemo(() => {
    const content = query.data?.content ?? [];
    // The reason filter's own `<Select>` only renders on the Waiting tab (§6.2) — applying it on any
    // other tab silently hid rows there too, with no visible control explaining why (defect found in
    // the WP5 review).
    if (tab !== 'waiting' || !reasonFilter) {
      return content;
    }
    return content.filter((row) => row.openEscalation?.reason === reasonFilter);
  }, [query.data, reasonFilter, tab]);

  const [lastUpdatedLabel, setLastUpdatedLabel] = useState('');
  useEffect(() => {
    const update = () => {
      if (!query.dataUpdatedAt) return;
      setLastUpdatedLabel(
        t('list.updatedAgo', {
          value: relativeTimeLabel(new Date(query.dataUpdatedAt).toISOString(), getIntlLocale()),
        }),
      );
    };
    update();
    const id = window.setInterval(update, 5000);
    return () => window.clearInterval(id);
  }, [query.dataUpdatedAt, t]);

  function resetFilters() {
    setReasonFilter('');
    updateParams({ channel: null, customerId: null, page: null });
  }

  const showFilters = tab !== 'closed';

  return (
    <PageContainer
      maxWidth={1200}
      title={t('list.title')}
      subtitle={t('list.subtitle')}
      actions={
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="caption" color="text.secondary">
            {lastUpdatedLabel}
          </Typography>
          <Tooltip title={t('list.refresh')}>
            <IconButton aria-label={t('list.refresh')} onClick={() => void query.refetch()}>
              <RefreshRoundedIcon />
            </IconButton>
          </Tooltip>
          {canViewChatbotSettings ? (
            <Tooltip title={t('list.chatbotSettings')}>
              <IconButton
                aria-label={t('list.chatbotSettings')}
                component={RouterLink}
                to="/settings/chatbot"
              >
                <SettingsRoundedIcon />
              </IconButton>
            </Tooltip>
          ) : null}
        </Stack>
      }
    >
      <Stack spacing={2} sx={{ height: '100%' }}>
        {!canViewPii ? (
          <Alert
            severity="info"
            icon={<VisibilityOffRoundedIcon fontSize="inherit" />}
            role="status"
          >
            {t('pii.pageNotice')}
          </Alert>
        ) : null}

        <Tabs
          value={tab}
          variant="scrollable"
          allowScrollButtonsMobile
          onChange={(_e, next: TabKey) => {
            // The reason filter is Waiting-only UI (§6.2) — carrying it silently into another tab is
            // exactly the leak this clears (regression test below).
            setReasonFilter('');
            updateParams({ tab: next, page: null });
          }}
        >
          <Tab
            value="waiting"
            label={
              <Badge
                color="error"
                badgeContent={queueCount.data?.totalElements ?? 0}
                max={99}
                invisible={!queueCount.data || queueCount.data.totalElements === 0}
                sx={{ pr: 1.5 }}
              >
                {t('list.tabs.waiting')}
              </Badge>
            }
          />
          <Tab value="operator" label={t('list.tabs.withOperator')} />
          <Tab value="open" label={t('list.tabs.open')} />
          <Tab value="closed" label={t('list.tabs.closed')} />
        </Tabs>

        {showFilters ? (
          <Paper variant="outlined" sx={{ p: 1.5, position: 'sticky', top: 16, zIndex: 1 }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.5}
              alignItems={{ md: 'center' }}
            >
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel id="conversations-filter-channel-label">
                  {t('list.filters.channel')}
                </InputLabel>
                <Select
                  labelId="conversations-filter-channel-label"
                  label={t('list.filters.channel')}
                  // MUI treats a bare '' Select value as its own "nothing selected" sentinel and
                  // never renders that MenuItem's label as selected — use a real sentinel for "All".
                  value={channelFilter || ALL_FILTER_VALUE}
                  onChange={(e) =>
                    updateParams({
                      channel: e.target.value === ALL_FILTER_VALUE ? null : e.target.value,
                      page: null,
                    })
                  }
                >
                  <MenuItem value={ALL_FILTER_VALUE}>{t('list.filters.all')}</MenuItem>
                  <MenuItem value="TELEGRAM">{t('list.filters.telegram')}</MenuItem>
                  <MenuItem value="MAX">{t('list.filters.max')}</MenuItem>
                </Select>
              </FormControl>
              {tab === 'waiting' ? (
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel id="conversations-filter-reason-label">
                    {t('list.filters.reason')}
                  </InputLabel>
                  <Select
                    labelId="conversations-filter-reason-label"
                    label={t('list.filters.reason')}
                    value={reasonFilter || ALL_FILTER_VALUE}
                    onChange={(e) =>
                      setReasonFilter(e.target.value === ALL_FILTER_VALUE ? '' : e.target.value)
                    }
                  >
                    <MenuItem value={ALL_FILTER_VALUE}>{t('list.filters.all')}</MenuItem>
                    {REASON_OPTIONS.map((reason) => (
                      <MenuItem key={reason} value={reason}>
                        {t(`reasons.${reason}.label`)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null}
              <TextField
                size="small"
                label={t('list.filters.customerId')}
                value={customerIdFilter}
                onChange={(e) => updateParams({ customerId: e.target.value || null, page: null })}
                sx={{ minWidth: 220 }}
                slotProps={{ htmlInput: { style: { fontFamily: 'JetBrains Mono, monospace' } } }}
              />
              <Button size="small" onClick={resetFilters}>
                {t('list.filters.reset')}
              </Button>
            </Stack>
          </Paper>
        ) : null}

        <Box sx={{ height: 4 }}>
          {query.isFetching && !query.isLoading ? <LinearProgress /> : null}
        </Box>

        {query.isLoading ? (
          <LoadingState label={t('list.loading')} />
        ) : query.isError ? (
          <ErrorState
            title={t('list.loadError')}
            message={query.error?.message}
            onRetry={() => void query.refetch()}
          />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<SupportAgentRoundedIcon sx={{ fontSize: 48 }} />}
            title={tab === 'waiting' ? t('list.empty.waiting.title') : t('list.empty.other.title')}
            description={
              tab === 'waiting'
                ? t('list.empty.waiting.description')
                : channelFilter || customerIdFilter
                  ? t('list.empty.other.filtered')
                  : tab === 'open'
                    ? t('list.empty.open')
                    : t('list.empty.other.filtered')
            }
            action={
              channelFilter || customerIdFilter ? (
                <Button onClick={resetFilters}>{t('list.filters.reset')}</Button>
              ) : undefined
            }
          />
        ) : (
          <TablePageShell
            footer={
              <CompactTablePagination
                count={query.data?.totalElements ?? 0}
                page={page}
                rowsPerPage={size}
                onPageChange={(_e, next) => updateParams({ page: String(next) })}
                onRowsPerPageChange={(e) => updateParams({ size: e.target.value, page: '0' })}
              />
            }
          >
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell component="th" scope="col">
                      {t('list.table.customerTopic')}
                    </TableCell>
                    <TableCell component="th" scope="col">
                      {t('list.table.channel')}
                    </TableCell>
                    <TableCell component="th" scope="col">
                      {t('list.table.reason')}
                    </TableCell>
                    <TableCell component="th" scope="col">
                      {tab === 'waiting' ? t('list.table.waiting') : t('list.table.lastMessage')}
                    </TableCell>
                    <TableCell component="th" scope="col">
                      {t('list.table.status')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => {
                    const { label, hidden } = customerLabel(row, canViewPii, t);
                    const topic = row.currentEpisode?.topic;
                    const isMerged = row.status === 'MERGED';
                    const waitingMs = row.openEscalation?.requestedAt
                      ? Date.now() - new Date(row.openEscalation.requestedAt).getTime()
                      : 0;
                    const waitingColor =
                      tab === 'waiting'
                        ? waitingMs >= WAITING_ERROR_MS
                          ? 'error.main'
                          : waitingMs >= WAITING_WARNING_MS
                            ? 'warning.main'
                            : 'text.primary'
                        : 'text.secondary';

                    return (
                      <TableRow
                        key={row.id}
                        hover
                        role="link"
                        tabIndex={0}
                        sx={{ cursor: 'pointer', opacity: isMerged ? 0.7 : 1 }}
                        onClick={() => navigate(`/conversations/${row.id}`)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') navigate(`/conversations/${row.id}`);
                        }}
                      >
                        <TableCell sx={{ maxWidth: 280 }}>
                          <RouterLink
                            to={`/conversations/${row.id}`}
                            onClick={(e) => e.stopPropagation()}
                            style={{ color: 'inherit', textDecoration: 'none' }}
                            aria-label={t('list.row.open', { name: topic ?? label })}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                              {topic ?? label}
                            </Typography>
                          </RouterLink>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            {hidden ? (
                              <Tooltip title={t('list.row.nameHidden')}>
                                <VisibilityOffRoundedIcon
                                  sx={{ fontSize: 14, color: 'text.disabled' }}
                                />
                              </Tooltip>
                            ) : null}
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {topic
                                ? label
                                : row.provisional
                                  ? t('list.row.notLinked')
                                  : row.id.slice(0, 8)}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          {row.sessions && row.sessions.length > 0 ? (
                            <Stack
                              direction="row"
                              spacing={0.5}
                              flexWrap="wrap"
                              useFlexGap
                              alignItems="center"
                            >
                              {row.sessions.map((session) => (
                                <ChannelBadge
                                  key={session.id}
                                  channel={session.channel}
                                  blocked={!session.reachable}
                                  size="small"
                                />
                              ))}
                              {row.sessions.every((session) => !session.reachable) ? (
                                <Tooltip title={t('list.row.unreachableTooltip')}>
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    icon={<BlockRoundedIcon fontSize="inherit" />}
                                    label={t('list.row.unreachable')}
                                  />
                                </Tooltip>
                              ) : null}
                            </Stack>
                          ) : (
                            // No sessions[] (an older BFF) — the single lastChannel badge (design §13.1).
                            <ChannelBadge channel={row.lastChannel} />
                          )}
                        </TableCell>
                        <TableCell>
                          {row.openEscalation ? (
                            <Stack spacing={0.25}>
                              <EscalationReasonChip reason={row.openEscalation.reason} />
                              {row.openEscalation.status === 'ACKED'
                                ? (() => {
                                    const acker = staffLabel(
                                      row.openEscalation?.ackedByRef,
                                      row.openEscalation?.ackedBy,
                                      user?.id ?? null,
                                      t,
                                    );
                                    if (!acker) return null;
                                    const caption = (
                                      <Typography variant="caption" color="text.secondary">
                                        {t('list.row.ackedBy', { name: acker.label })}
                                      </Typography>
                                    );
                                    return acker.tooltip ? (
                                      <Tooltip title={acker.tooltip}>{caption}</Tooltip>
                                    ) : (
                                      caption
                                    );
                                  })()
                                : null}
                            </Stack>
                          ) : (
                            <Typography variant="body2" color="text.disabled">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {tab === 'waiting' && row.openEscalation ? (
                            <Stack spacing={0.25}>
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                {waitingMs >= WAITING_WARNING_MS ? (
                                  <WarningAmberRoundedIcon
                                    sx={{ fontSize: 14, color: waitingColor }}
                                  />
                                ) : (
                                  <ScheduleRoundedIcon sx={{ fontSize: 14, color: waitingColor }} />
                                )}
                                <RelativeTime
                                  value={row.openEscalation.requestedAt}
                                  variant="body2"
                                  sx={{ color: waitingColor }}
                                />
                              </Stack>
                              {row.openEscalation.expiresAt ? (
                                <Typography variant="caption" color="text.secondary">
                                  {t('list.row.expiresIn', {
                                    value: formatDurationShort(
                                      new Date(row.openEscalation.expiresAt).getTime() - Date.now(),
                                      getIntlLocale(),
                                    ),
                                  })}
                                </Typography>
                              ) : null}
                            </Stack>
                          ) : (
                            <Stack spacing={0.25} sx={{ maxWidth: 220 }}>
                              <RelativeTime
                                value={
                                  row.lastInboundAt ??
                                  row.lastOutboundAt ??
                                  new Date().toISOString()
                                }
                                variant="body2"
                                color="text.secondary"
                              />
                              {canViewPii && row.lastMessagePreview ? (
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {row.lastMessagePreview}
                                </Typography>
                              ) : null}
                            </Stack>
                          )}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <ConversationStatusChip
                              status={row.currentEpisode?.status ?? row.status}
                            />
                            {row.currentEpisode?.idle ? (
                              <IdleChip lastActivityAt={row.currentEpisode.lastActivityAt} />
                            ) : null}
                          </Stack>
                          {row.currentEpisode ? (
                            <Typography variant="caption" color="text.secondary">
                              {t('list.row.since', {
                                value: formatDayTime(row.currentEpisode.startedAt, getIntlLocale()),
                              })}
                            </Typography>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </TablePageShell>
        )}
      </Stack>
    </PageContainer>
  );
}
